import { useState } from "react"
import { Home, ListChecks, Users, User, Building, LogOut, Shield, Images, Share, Sparkles, Briefcase, Newspaper, Link2, Lightbulb, Crosshair, Columns3, Palette, CalendarDays, CalendarClock, Youtube, Instagram, ListTree, Image as ImageIcon, Clapperboard, Linkedin, BadgeCheck, Lock, ChevronRight } from "lucide-react"
import { Link, useLocation } from "wouter"
import { useQuery } from "@tanstack/react-query"
import { useToast } from "@/hooks/use-toast"
import { apiRequest } from "@/lib/queryClient"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { useAuth } from "@/hooks/useAuth"
import type { User as UserType } from "@shared/schema"
import { checkinProgress, type CheckinStatus } from "@/lib/checkin"
import { useEdition } from "@/lib/edition"
import { buildNav, LOCKED_MODULE_HINT, type NavIcon, type NavItem, type NavSection } from "@/lib/navModel"

// The menu itself (sections, labels, which entries a company sees) comes from lib/navModel.ts.
const ICONS: Record<NavIcon, typeof Home> = {
  home: Home, checkin: ListChecks, photos: Images, sparkles: Sparkles, news: Newspaper, napkin: Lightbulb,
  link: Link2, columns: Columns3, palette: Palette, calendar: CalendarDays, calendarClock: CalendarClock,
  youtube: Youtube, instagram: Instagram, user: User, building: Building, share: Share, briefcase: Briefcase,
  crosshair: Crosshair, users: Users, topics: ListTree, image: ImageIcon, clapperboard: Clapperboard,
  linkedin: Linkedin, brand: BadgeCheck,
}

const testId = (title: string) => `sidebar-${title.toLowerCase().replace(/\s+/g, '-')}`

// Admin-only navigation items
const adminNavigationItems = [
  {
    title: "Admin Dashboard",
    url: "/admin",
    icon: Shield,
  },
]

export function AppSidebar() {
  const [location] = useLocation()
  const { user } = useAuth()
  const userData = user as UserType | undefined
  const { setOpenMobile } = useSidebar()
  const { toast } = useToast()
  
  // Close sidebar on mobile after navigation
  const closeMobileSidebar = () => {
    setOpenMobile(false)
  }
  
  // Fetch pending users count for admin badge (only if user is admin)
  const { data: pendingData } = useQuery<{ count: number }>({
    queryKey: ['/api/admin/pending-users-count'],
    enabled: userData?.role === 'admin',
    refetchInterval: 30000, // Refresh every 30 seconds
  })
  
  const pendingUsersCount = pendingData?.count || 0

  // DFY tier gate for the Wochenplan nav item (plan item 19: sidebar only if tier==='dfy').
  const { data: companyProfile } = useQuery<{ tier?: string; checklistDismissedAt?: string | null }>({ queryKey: ['/api/companies/me'] })
  const isDfy = companyProfile?.tier === 'dfy'
  // Check-in nav item: agencies only while the check-in is not hidden, hotels always (navModel); shares the
  // status query with the page.
  const editionState = useEdition()
  const isHotelMenu = editionState.status === "ready" && editionState.edition.kind === "hotel"
  const showCheckin = !!companyProfile && !companyProfile.checklistDismissedAt
  const { data: checkinStatus } = useQuery<CheckinStatus>({ queryKey: ['/api/onboarding/status'], enabled: showCheckin || (!!companyProfile && isHotelMenu), retry: false })
  const checkinCount = checkinProgress(checkinStatus).done

  // Signal-Room gate for the Instagram-Säulen item — same queryKey as the page, so React Query
  // dedupes the fetch. Unmapped companies (or the kill switch off) never see the item.
  const { data: srPillars } = useQuery<{ mapped?: boolean }>({ queryKey: ['/api/signal-room/instagram-pillars'] })
  const isSignalRoomMapped = srPillars?.mapped === true

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U'
  }

  const handleLogout = async () => {
    try {
      await apiRequest('POST', '/api/auth/logout')
    } catch (err) {
      console.error("Logout request failed", err)
    }
    window.location.href = "/"
  }

  // Edition (PLAN.md §2): the hotel menu, the agency menu, or a neutral view while the capability loads.
  const nav = buildNav({
    edition: editionState.status === "ready" ? editionState.edition : editionState.status === "error" ? "error" : null,
    isDfy,
    isAdmin: userData?.role === 'admin',
    srMapped: isSignalRoomMapped,
    checkinOpen: showCheckin,
  })

  const showLockedHint = () => {
    toast({ title: "Zusatzmodul", description: LOCKED_MODULE_HINT })
  }

  const renderItem = (item: NavItem) => {
    const Icon = ICONS[item.icon]
    if (item.locked) {
      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton
            isActive={false}
            onClick={showLockedHint}
            aria-disabled="true"
            title={LOCKED_MODULE_HINT}
            className="text-muted-foreground"
            data-testid={`${testId(item.title)}-locked`}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1">{item.title}</span>
            <Lock className="h-3.5 w-3.5 ml-auto" aria-hidden="true" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      )
    }
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          asChild
          isActive={location === item.url}
          data-testid={testId(item.title)}
        >
          <Link href={item.url} onClick={closeMobileSidebar}>
            <Icon className="h-4 w-4" />
            <span className="flex-1">{item.title}</span>
            {item.checkinBadge && (
              <Badge variant="secondary" className="ml-auto text-[11px]" data-testid="badge-checkin-progress">{checkinCount}/6</Badge>
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  const sectionId = (label: string) => label.toLowerCase().replace(/\s+/g, '-')

  const renderSection = (section: NavSection) => {
    if (section.collapsible) {
      return (
        <Collapsible defaultOpen={false} className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel
              asChild
              className="text-[11px] uppercase text-[#999] font-medium tracking-wider mt-3"
              data-section={sectionId(section.label)}
            >
              <CollapsibleTrigger className="flex w-full items-center" data-testid={`sidebar-group-${sectionId(section.label)}`}>
                {section.label}
                <ChevronRight className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              {section.note && (
                <p className="px-2 pb-2 text-xs text-muted-foreground" data-testid="sidebar-locked-note">{section.note}</p>
              )}
              <SidebarGroupContent>
                <SidebarMenu>{section.items.map(renderItem)}</SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      )
    }
    return (
      <SidebarGroup>
        <SidebarGroupLabel 
          className="text-[11px] uppercase text-[#999] font-medium tracking-wider mt-3"
          data-section={sectionId(section.label)}
        >
          {section.label}
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>{section.items.map(renderItem)}</SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    )
  }

  return (
    <Sidebar>
      <SidebarHeader className="border-b p-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">A</span>
          </div>
          {nav.kind === "menu" ? (
            <div>
              {/* The longer hotel title fits the 14rem sidebar on one line at text-sm. */}
              <h2 className={nav.header.title.length > 14 ? "font-bold text-sm leading-tight whitespace-nowrap" : "font-bold text-lg"}>{nav.header.title}</h2>
              {nav.header.subtitle && (
                <p
                  className={nav.header.emphasis ? "text-sm font-bold text-primary" : "text-sm text-muted-foreground"}
                  data-testid="sidebar-edition-label"
                >
                  {nav.header.subtitle}
                </p>
              )}
            </div>
          ) : (
            <div className="flex-1 space-y-1.5" data-testid="sidebar-header-loading">
              <div className="h-4 w-28 rounded-md bg-sidebar-accent" />
              <div className="h-3 w-20 rounded-md bg-sidebar-accent" />
            </div>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        {nav.kind === "loading" ? (
          <SidebarGroup data-testid="sidebar-loading">
            <SidebarGroupContent>
              <SidebarMenu>
                {Array.from({ length: 8 }, (_, i) => (
                  <SidebarMenuItem key={i}><SidebarMenuSkeleton showIcon /></SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : nav.sections.filter((section) => section.items.length > 0).map((section, sectionIndex) => (
          <div key={section.label}>
            {sectionIndex > 0 && (
              <Separator className="my-3" />
            )}
            {renderSection(section)}
          </div>
        ))}

        {/* Admin Section - Only visible to admin users */}
        {userData?.role === 'admin' && (
          <>
            <Separator className="my-3" />
            <SidebarGroup>
              <SidebarGroupLabel className="text-[11px] uppercase text-[#999] font-medium tracking-wider mt-3">
                Administration
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {adminNavigationItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton 
                        asChild 
                        isActive={location === item.url}
                        data-testid={`sidebar-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Link href={item.url} onClick={closeMobileSidebar}>
                          <item.icon className="h-4 w-4" />
                          <span className="flex-1">{item.title}</span>
                          {pendingUsersCount > 0 && (
                            <Badge 
                              variant="destructive" 
                              className="ml-auto h-5 min-w-5 px-1.5 text-xs font-medium"
                              data-testid="badge-pending-users"
                            >
                              {pendingUsersCount}
                            </Badge>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
      <SidebarFooter className="border-t p-4">
        {userData && (
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Avatar className="h-8 w-8">
                <AvatarImage src={userData.profileImageUrl || ""} />
                <AvatarFallback>
                  {getInitials(userData.firstName, userData.lastName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {userData.firstName || userData.lastName ? 
                    `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 
                    'User'
                  }
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {userData.email || ''}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="w-full justify-start"
              data-testid="button-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Abmelden
            </Button>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
